/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useRef } from 'react';
import {
  WMLayer,
  webmapUtils,
  LayerType,
  WMJSDimension,
} from '@opengeoweb/webmap';
import type { OnInitializeLayerProps } from '../../components/OpenLayers/utils/types';
import { useStacCollection, useStacTemporalStepItems } from '../hooks';
import { inferTemporalStep } from './stacTimeStepUtils';

const hasTemporalResolution = (
  collectionProperties?: Record<string, unknown>,
): boolean => typeof collectionProperties?.['temporal-resolution'] === 'string';

/**
 * Returns WMLayer instance in STAC GeoTIFF mode. The layer will contains parsed dimensions and styles and keeps a state for these properties.
 *
 * @param {string} serviceUrl
 * @param {string} name
 * @returns {(WMLayer | null)}
 */
export const useGetStacGeoTIFFWMLayerInstance = (
  serviceUrl: string,
  name: string,
  id?: string,
  onLayerError?: (layerId: string, message: string) => void,
): WMLayer | null => {
  const [layer, setLayer] = React.useState<WMLayer | null>(null);
  const refId = useRef<string>(id || webmapUtils.generateLayerId());
  React.useEffect((): void => {
    const layerId = refId.current;
    // Check if the layer is already registered
    const tmpWMLayer = webmapUtils.getWMLayerById(layerId);
    if (!tmpWMLayer || (tmpWMLayer.service !== serviceUrl && name.length > 0)) {
      // Make a new layer if the serviceUrl changes. (But not when the name changes)
      const wmLayer = new WMLayer({
        name,
        service: serviceUrl,
        layerType: LayerType.stacGeoTIFFLayer,
        id: layerId,
      });
      webmapUtils.registerWMLayer(wmLayer, layerId);
    }

    // Set the name and parse the layer
    const wmLayer = webmapUtils.getWMLayerById(layerId);

    wmLayer
      .setName(name)
      .then(() => {
        setLayer(wmLayer);
      })
      .catch((e) => {
        setLayer(wmLayer);
        onLayerError?.(layerId, e);
      });
  }, [name, onLayerError, serviceUrl]);

  return layer;
};
export const useGetStacGeoTIFFLayerInstance = (
  stacCollectionURL: string,
  parameter: string,
  layerId?: string,
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void,
): WMLayer | null => {
  const wmLayer = useGetStacGeoTIFFWMLayerInstance(
    stacCollectionURL,
    parameter,
    layerId,
  );

  const collectionInfo = useStacCollection(stacCollectionURL);
  const [timeRangeDuration, setTimeRangeDuration] = React.useState<
    string | undefined
  >(undefined);
  const temporalInterval = collectionInfo.data?.extent?.temporal?.interval?.[0];
  const extentStart = temporalInterval?.[0];
  const fallbackExtentEnd = React.useMemo(
    () => (collectionInfo.data ? new Date().toISOString() : undefined),
    [collectionInfo.data],
  );
  const extentEnd = temporalInterval?.[1] || fallbackExtentEnd;
  const shouldFetchTemporalStepItems =
    collectionInfo.isFetched &&
    !!extentStart &&
    !!extentEnd &&
    !hasTemporalResolution(collectionInfo.data?.properties);
  const temporalStepItems = useStacTemporalStepItems(
    stacCollectionURL,
    extentStart || undefined,
    extentEnd,
    shouldFetchTemporalStepItems,
  );
  const temporalStepItemsReady =
    !shouldFetchTemporalStepItems ||
    temporalStepItems.isFetched ||
    temporalStepItems.isError;

  React.useEffect(() => {
    if (
      wmLayer &&
      collectionInfo.data &&
      collectionInfo.isFetched &&
      extentStart &&
      extentEnd &&
      temporalStepItemsReady
    ) {
      // Infer temporal step from collection metadata and items instead of hardcoding a fixed step.
      const temporalStep = inferTemporalStep(
        collectionInfo.data,
        temporalStepItems.data,
      );
      const timeRangeDurationFromStacCollection = `${extentStart}/${extentEnd}/${temporalStep}`;

      if (!wmLayer.getDimension('time')) {
        const dim = new WMJSDimension({
          name: 'time',
          currentValue: extentEnd,
          units: 'ISO8601',
          values: timeRangeDurationFromStacCollection,
        });
        dim.initialize();
        wmLayer.dimensions.push(dim);
      } else {
        wmLayer
          .getDimension('time')
          ?.reInitializeValues(timeRangeDurationFromStacCollection);
      }
      setTimeRangeDuration(timeRangeDurationFromStacCollection);
      wmLayer.isConfigured = true;
    }
  }, [
    collectionInfo.data,
    collectionInfo.isFetched,
    extentEnd,
    extentStart,
    temporalStepItems.data,
    temporalStepItemsReady,
    wmLayer,
  ]);

  React.useEffect(() => {
    const initializeLayer = async (): Promise<void> => {
      if (wmLayer) {
        // Set time dimension current value based on layerprops dimension value
        const wmTimeDim = wmLayer?.dimensions?.find(
          (wmDimension) => wmDimension.name === 'time',
        );

        const timeValueToInitialize = wmTimeDim?.getValue();
        wmTimeDim &&
          timeValueToInitialize &&
          wmTimeDim.setValue(timeValueToInitialize);
        const layerProps = {
          layerId: wmLayer.id,
          dimensions: wmLayer.dimensions.map((dimension) => {
            return {
              name: dimension.name,
              units: dimension.units,
              currentValue: dimension.currentValue,
              maxValue: dimension.getLastValue(),
              minValue: dimension.getFirstValue(),
              synced: dimension.synced,
              values: dimension.values,
              unitSymbol: dimension.unitSymbol,
            };
          }),
          style: '',
        };
        onInitializeLayer?.(layerProps);
      }
    };

    timeRangeDuration &&
      initializeLayer().catch((e) => window.console.error(e));
    return (): void => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wmLayer, parameter, timeRangeDuration]);

  return wmLayer;
};
